<script setup>
import FilterSection from "@/components/Dashboard/FilterSection.vue";
import Loading from "@/components/Loading.vue";
import DataTable from "@/components/DataTable.vue";
import { API_PATHS } from "@/utils/apiPaths";
import axiosInstance from "@/utils/axiosInstance";
import { ref } from "vue";
import { onMounted } from "vue";
import Swal from "sweetalert2";
import { toast } from "vue3-toastify";
import TableNavigation from "@/components/TableNavigation.vue";
import BaseModal from "@/components/BaseModal.vue";
import BaseInput from "@/components/BaseInput.vue";
import BaseTextArea from "@/components/BaseTextArea.vue";
import { Ban, Save } from "lucide-vue-next";
import BaseButton from "@/components/BaseButton.vue";

const department = ref([]);
const total = ref(null);
const perPage = ref(10);
const current_page = ref();
const loading = ref(false);
const modalOpen = ref(false);
const columns = [
  {
    key: "",
    title: "#",
  },
  {
    key: "dep_name",
    title: "departamento",
  },
  {
    key: "description",
    title: "Descrição",
  },
  {
    title: "Ações",
    key: "actions",
  },
];

const formDatas = ref({
  id: "",
  dep_name: "",
  description: "",
});

const getDepartements = async (page = 1) => {
  try {
    loading.value = true;
    const params = new URLSearchParams();
    if (page) {
      params.append("page", page);
    }

    const resp = await axiosInstance.get(
      API_PATHS.DEPARTMENTS.getDepartmentWithPagination(params)
    );

    department.value = resp.data.data;
    total.value = resp.data.total;
    perPage.value = resp.data.per_page;
    current_page.value = resp.data.current_page;
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
};

function handleEdit(row) {
  modalOpen.value = true;

  formDatas.value.id = row.id;
  formDatas.value.dep_name = row.dep_name;
  formDatas.value.description = row.description;
}

function confirmBeforeDelete(row) {
  Swal.fire({
    title: "Tem certeza que deseja eliminar este registro?",
    text: "Não tera como reverter esta operação!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Sim, Eliminar!",
    cancelButtonText: "Cancel!",
  }).then((result) => {
    if (result.isConfirmed) {
      deleteDepartment(row);
    }
  });
}

async function deleteDepartment(row) {
  try {
    loading.value = false;

    const response = await axiosInstance.delete(
      API_PATHS.DEPARTMENTS.DeleteDepartment(row.id)
    );

    if (response.data == 1) {
      toast.success("Registro Eliminado com Sucesso.", {
        autoClose: 1000,
      });
    }
    const index = department.value.findIndex((item) => item.id === row.id);

    if (index != -1) {
      //   department.value.filter((item) => item.id != row.id);
      department.value.splice(index, 1);
      total.value -= 1;
    }
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
}

async function UpdateDept() {
  try {
    loading.value = true;

    if (formDatas.value.dep_name == "") {
      toast.info("O campo chefe do departmento nao estar vazio!");
      loading.value = false;
      return;
    }

    if (formDatas.value.person_id == "") {
      toast.info("O campo chefe do departmento nao estar vazio!");
      loading.value = false;
      return;
    }

    var dept = department.value.find(
      (item) =>
        item.dep_name == formDatas.value.dep_name &&
        item.id != formDatas.value.id
    );

    if (dept) {
      return toast.info("Este departamento ja existe.");
    }

    const response = await axiosInstance.patch(
      API_PATHS.DEPARTMENTS.updateDepartment(formDatas.value.id),
      formDatas.value
    );

    const index = department.value.findIndex(
      (item) => item.id == formDatas.value.id
    );

    if (index !== -1) {
      department.value[index] = {
        ...department.value[index],
        ...formDatas.value,
      };
    }

    if (response.data.success == true) {
      toast.success(response.data.message, { autoClose: 1000 });
      modalOpen.value = false;
    }
  } catch (error) {
    if (typeof error.response != "undefined") {
      Object.keys(error.response.data.errors).forEach((key) => {
        toast.info(key, error.response.data.errors[key]);
      });
    }
    console.log(error);
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  getDepartements();
});
</script>

<template>
  <div class="max-w-7xl">
    <Loading :loading-data="loading" />
    <FilterSection title="Gerenciamentos de Departamentos" />
    <div>
      <DataTable :columns="columns" :data="department">
        <template #actions="{ row }">
          <div class="flex gap-2">
            <button class="text-blue-500" @click="handleEdit(row)">
              Editar
            </button>
            <button class="text-red-500" @click="confirmBeforeDelete(row)">
              Excluir
            </button>
          </div>
        </template>
      </DataTable>
      <TableNavigation
        :currentPage="current_page"
        :perPage="perPage"
        :totalItems="total"
        @page-change="
          (page) => {
            current_page = page;
            getDepartements(page);
          }
        "
      />
    </div>
    <div class="bg-white col col-span-12">
      <BaseModal
        v-model:modal-value="modalOpen"
        @update:modal-value="(val) => (modalOpen = val)"
        title="Actualizar informacao do departamento"
        width="w-full sm:w-[800px]"
        height="max-h-[600px]"
      >
        <div class="col col-span-10">
          <BaseInput label="Titulo" v-model="formDatas.dep_name" />
          <BaseTextArea
            :row="10"
            :cols="16"
            label="Descrição"
            v-model="formDatas.description"
          />
        </div>
        <div class="flex justify-end gap-2">
          <BaseButton
            :font="'font-bold'"
            :bg-color="'bg-green-700'"
            :label="loading ? 'Aguarde...' : 'Salvar'"
            :rounded="'rounded-full'"
            :shadow="'shadow-md'"
            :disabled="loading"
            :textColor="'text-white'"
            :icon="'Save'"
            :handleClick="() => UpdateDept()"
          />
          <BaseButton
            :font="'font-bold'"
            :bg-color="'bg-red-500'"
            :label="'Cancelar'"
            :rounded="'rounded-full'"
            :shadow="'shadow-md'"
            :disabled="loading"
            :textColor="'text-white'"
            :icon="'Ban'"
            :handleClick="() => (modalOpen = false)"
          />
        </div>
      </BaseModal>
    </div>
  </div>
</template>